import { useCallback, useEffect, useRef, useState } from "react";
import { customFetch } from "@workspace/api-client-react";
import {
  CLOCK_RESAMPLE_INTERVAL_MS,
  CLOCK_SAMPLE_COUNT,
  correctedNow,
  estimateClockOffset,
} from "@workspace/player-core";
import { collectClockSamples } from "@/player/lib/clock-sampling";

interface ServerTimeResponse {
  serverTimeMs: number;
}

/**
 * One round-trip against the public server clock. Goes through customFetch so
 * the request follows the same base-URL resolution as every other player call
 * (same-origin `/api` on web, the configured server origin on desktop /
 * VITE_API_URL). `cache: "no-store"` keeps HTTP caches from serving a stale
 * timestamp, which would poison the offset estimate.
 */
async function fetchServerTimeMs(): Promise<number> {
  const res = await customFetch<ServerTimeResponse>("/api/device/time", {
    responseType: "json",
    cache: "no-store",
  });
  const ms = res?.serverTimeMs;
  if (typeof ms !== "number" || !Number.isFinite(ms)) {
    throw new Error("invalid serverTimeMs");
  }
  return ms;
}

export interface ClockOffsetHandle {
  /** Latest estimate (ms to add to the local clock), null until the first burst lands. */
  offsetMs: number | null;
  /** Server-clock approximation of "now" (local clock until an estimate exists). */
  correctedNowDate: () => Date;
}

/**
 * Keep an NTP-style estimate of the server clock offset warm: burst-sample
 * GET /api/device/time on mount and every CLOCK_RESAMPLE_INTERVAL_MS, keeping
 * the minimum-RTT estimate (see player-core clock-offset.ts). Consumers use
 * `correctedNowDate()` wherever timeline math needs the server's wall clock.
 */
export function useClockOffset(enabled: boolean): ClockOffsetHandle {
  const [offsetMs, setOffsetMs] = useState<number | null>(null);
  const offsetRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let sampling = false;

    const sample = async (): Promise<void> => {
      if (sampling) return;
      sampling = true;
      try {
        const samples = await collectClockSamples(
          CLOCK_SAMPLE_COUNT,
          fetchServerTimeMs,
        );
        if (disposed) return;
        const estimate = estimateClockOffset(samples);
        // A dead burst (offline, server unreachable) keeps the last estimate:
        // a stale offset beats reverting to the raw local clock mid-playback.
        if (estimate) {
          offsetRef.current = estimate.offsetMs;
          setOffsetMs(estimate.offsetMs);
        }
      } finally {
        sampling = false;
      }
    };

    void sample();
    const timer = window.setInterval(() => {
      void sample();
    }, CLOCK_RESAMPLE_INTERVAL_MS);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [enabled]);

  const correctedNowDate = useCallback((): Date => {
    const offset = offsetRef.current;
    return new Date(
      offset === null ? Date.now() : correctedNow(offset, Date.now()),
    );
  }, []);

  return { offsetMs, correctedNowDate };
}
